import React, { useEffect, useRef, useState } from "react";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { isAuthenticated, logout } from "../services/auth";
import { openDemoModal } from "../utils/demoModal";
import { useCases, industries, resources } from "./navdata";
// Header logo (navy text, for the light header). If it looks wrong, try
// VIRIQO_HIRES_Logo.png instead.
import logo from "../../interviewAI/assets/newlogo/VIRIQO_HIRES_Logo.png";
import "./Navbar.css";

/* ---------- icons (same paths as the HTML reference) ---------- */
const ICONS = {
  users: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></>),
  code: (<path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />),
  filter: (<path d="M3 4h18l-7 8v6l-4 2v-8z" />),
  globe: (<><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>),
  cap: (<><path d="M22 9L12 4 2 9l10 5 10-5z" /><path d="M6 11.5V16c3 2 9 2 12 0v-4.5" /></>),
  bank: (<path d="M3 10l9-6 9 6M5 10v8M9 10v8M15 10v8M19 10v8M3 21h18" />),
  heart: (<><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21.2l8.8-8.8a5.5 5.5 0 0 0 0-7.8z" /><path d="M3 12h4l2-3 3 6 2-3h7" /></>),
  chip: (<><rect x="5" y="5" width="14" height="14" rx="2" /><path d="M9 9h6v6H9zM9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" /></>),
  cart: (<><circle cx="9" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" /><path d="M2 3h3l2.7 12.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.5L22 7H6" /></>),
  factory: (<><path d="M2 20V9l6 4V9l6 4V4h8v16z" /><path d="M6 17h2M11 17h2M16 17h2" /></>),
  doc: (<><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>),
  chat: (<path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />),
};

const Icon = ({ name }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

const Chevron = () => (
  <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [authed, setAuthed] = useState(isAuthenticated());
  const [menuOpen, setMenuOpen] = useState(false); // mobile panel
  const [scrolled, setScrolled] = useState(false);
  const [openDrop, setOpenDrop] = useState(null); // desktop dropdown: "solutions" | "resources" | null
  const [mobileSub, setMobileSub] = useState({}); // mobile accordions
  const closeTimer = useRef(null);

  // Solid header on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Refresh auth state when route changes
  useEffect(() => {
    setAuthed(isAuthenticated());
  }, [location.pathname]);

  // Close menus after navigation
  useEffect(() => {
    setMenuOpen(false);
    setOpenDrop(null);
  }, [location.pathname, location.hash]);

  // Escape closes dropdowns and the mobile menu
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenDrop(null);
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Click outside closes desktop dropdowns
  useEffect(() => {
    if (!openDrop) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest(".vh-menu .has-drop")) setOpenDrop(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [openDrop]);

  // Lock page scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1080) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* ---------- desktop dropdown helpers (hover, click, keyboard) ---------- */
  const openDropdown = (key) => {
    clearTimeout(closeTimer.current);
    setOpenDrop(key);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenDrop(null), 120);
  };
  const toggleDropdown = (key) => {
    clearTimeout(closeTimer.current);
    setOpenDrop((cur) => (cur === key ? null : key));
  };
  const dropProps = (key) => ({
    className: `has-drop${openDrop === key ? " on" : ""}`,
    onMouseEnter: () => openDropdown(key),
    onMouseLeave: scheduleClose,
    onBlur: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) scheduleClose();
    },
  });

  const closeAll = () => {
    setOpenDrop(null);
    setMenuOpen(false);
  };

  /* ---------- actions ---------- */
  const handleLogoClick = (event) => {
    closeAll();
    if (location.pathname !== "/") return; // let the router navigate home
    event.preventDefault();
    window.history.replaceState(null, "", "/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLogout = async () => {
    await logout();
    setAuthed(false);
    setMenuOpen(false);
    navigate("/", { replace: true });
  };

  const handleRequestDemo = () => {
    setMenuOpen(false);
    openDemoModal();
  };

  /* ---------- active state ---------- */
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const solutionsActive = isActive("/solutions");
  const resourcesActive = resources.some((r) => isActive(r.path));
  const isAuthRoute = ["/login", "/signup", "/forgot-password", "/reset-password"].some(
    (path) => location.pathname.startsWith(path)
  );

  const linkCls = (active) => `vh-link${active ? " active" : ""}`;
  const mLinkCls = (active) => `vh-m-link${active ? " active" : ""}`;

  const toggleMobileSub = (key) =>
    setMobileSub((cur) => ({ ...cur, [key]: !cur[key] }));

  return (
    <>
      {/* =================== HEADER =================== */}
      <header
        className={`vh-header${isAuthRoute ? " auth-route" : ""}${scrolled ? " scrolled" : ""}${menuOpen ? " open" : ""}`}
        id="vhHeader"
      >
        <div className="vh-wrap">
          <RouterLink
            to="/"
            className="vh-logo"
            aria-label="Viriqo Hires home"
            onClick={handleLogoClick}
          >
            <img src={logo} alt="Viriqo Hires" />
          </RouterLink>

          <nav aria-label="Main">
            <ul className="vh-menu">
              <li>
                <RouterLink
                  className={linkCls(isActive("/features"))}
                  to="/features"
                  aria-current={isActive("/features") ? "page" : undefined}
                >
                  Features
                </RouterLink>
              </li>

              {/* Solutions mega menu */}
              <li {...dropProps("solutions")}>
                <button
                  type="button"
                  className={linkCls(solutionsActive)}
                  aria-expanded={openDrop === "solutions"}
                  aria-controls="dropSolutions"
                  onClick={() => toggleDropdown("solutions")}
                >
                  Solutions <Chevron />
                </button>
                <div className="vh-drop mega" id="dropSolutions">
                  <div className="vh-cols">
                    <div>
                      <div className="vh-col-h">By Use Case</div>
                      {useCases.map((item) => (
                        <RouterLink key={item.path} className="vh-item" to={item.path} onClick={closeAll}>
                          <span className="ic"><Icon name={item.icon} /></span>
                          <span><b>{item.label}</b><small>{item.desc}</small></span>
                        </RouterLink>
                      ))}
                    </div>
                    <div>
                      <div className="vh-col-h">By Industry</div>
                      {industries.map((item) => (
                        <RouterLink key={item.path} className="vh-item" to={item.path} onClick={closeAll}>
                          <span className="ic"><Icon name={item.icon} /></span>
                          <span><b>{item.label}</b><small>{item.desc}</small></span>
                        </RouterLink>
                      ))}
                    </div>
                  </div>
                  <div className="vh-drop-foot">
                    <span>For technical and non-technical hiring.</span>
                    <RouterLink className="vh-more" to="/solutions" onClick={closeAll}>
                      View All Solutions <Arrow />
                    </RouterLink>
                  </div>
                </div>
              </li>

              {/* Resources dropdown */}
              <li {...dropProps("resources")}>
                <button
                  type="button"
                  className={linkCls(resourcesActive)}
                  aria-expanded={openDrop === "resources"}
                  aria-controls="dropResources"
                  onClick={() => toggleDropdown("resources")}
                >
                  Resources <Chevron />
                </button>
                <div className="vh-drop small" id="dropResources">
                  {resources.map((item) => (
                    <RouterLink key={item.path} className="vh-item" to={item.path} onClick={closeAll}>
                      <span className="ic"><Icon name={item.icon} /></span>
                      <span><b>{item.label}</b><small>{item.desc}</small></span>
                    </RouterLink>
                  ))}
                </div>
              </li>

              <li>
                <RouterLink
                  className={linkCls(isActive("/about"))}
                  to="/about"
                  aria-current={isActive("/about") ? "page" : undefined}
                >
                  About
                </RouterLink>
              </li>
            </ul>
          </nav>

          <div className="vh-actions">
            {authed ? (
              <button type="button" className="vh-signin" onClick={handleLogout}>
                Logout
              </button>
            ) : (
              <RouterLink className="vh-signin" to="/login">
                Sign In
              </RouterLink>
            )}
            <button type="button" className="vh-btn vh-btn-primary" onClick={handleRequestDemo}>
              Book a Demo <Arrow />
            </button>
            <button
              type="button"
              className="vh-burger"
              id="vhBurger"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="vhMobile"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* =================== MOBILE PANEL =================== */}
      <div className="vh-mobile" id="vhMobile" aria-label="Mobile menu">
        <ul className="vh-m-list">
          <li>
            <RouterLink className={mLinkCls(isActive("/features"))} to="/features" onClick={closeAll}>
              Features
            </RouterLink>
          </li>

          <li className={mobileSub.solutions ? "on" : ""}>
            <button
              type="button"
              className={mLinkCls(solutionsActive)}
              aria-expanded={!!mobileSub.solutions}
              onClick={() => toggleMobileSub("solutions")}
            >
              Solutions <Chevron />
            </button>
            <div className="vh-m-sub">
              <div>
                <div className="vh-m-group">
                  <div className="vh-col-h">By Use Case</div>
                  {useCases.map((item) => (
                    <RouterLink key={item.path} to={item.path} onClick={closeAll}>{item.label}</RouterLink>
                  ))}
                </div>
                <div className="vh-m-group">
                  <div className="vh-col-h">By Industry</div>
                  {industries.map((item) => (
                    <RouterLink key={item.path} to={item.path} onClick={closeAll}>{item.label}</RouterLink>
                  ))}
                </div>
                <RouterLink className="vh-more" to="/solutions" onClick={closeAll}>
                  View All Solutions <Arrow />
                </RouterLink>
              </div>
            </div>
          </li>

          <li className={mobileSub.resources ? "on" : ""}>
            <button
              type="button"
              className={mLinkCls(resourcesActive)}
              aria-expanded={!!mobileSub.resources}
              onClick={() => toggleMobileSub("resources")}
            >
              Resources <Chevron />
            </button>
            <div className="vh-m-sub">
              <div className="vh-m-group">
                {resources.map((item) => (
                  <RouterLink key={item.path} to={item.path} onClick={closeAll}>{item.label}</RouterLink>
                ))}
              </div>
            </div>
          </li>

          <li>
            <RouterLink className={mLinkCls(isActive("/about"))} to="/about" onClick={closeAll}>
              About
            </RouterLink>
          </li>
        </ul>

        <div className="vh-m-actions">
          {authed ? (
            <button type="button" className="vh-btn vh-out" onClick={handleLogout}>
              Logout
            </button>
          ) : (
            <RouterLink className="vh-btn vh-out" to="/login" onClick={closeAll}>
              Sign In
            </RouterLink>
          )}
          <button type="button" className="vh-btn vh-btn-primary" onClick={handleRequestDemo}>
            Book a Demo <Arrow />
          </button>
        </div>
      </div>
    </>
  );
};

export default Navbar;
